import {
    PropsWithChildren,
    createContext,
    useContext,
    useEffect,
    useState
} from "react";

import {
    UserProfile,
    useBxpWebStargateClient
} from "@/libraries/bxp-web-stargate-client/src";

export type LoggedInUser = UserProfile & {
    isUserLoggedIn: boolean;
    requestInProgress?: boolean;
};

const DEFAULT_LOGGED_OUT_USER_STATE: LoggedInUser = {
    accountId: "",
    email: "",
    isUserLoggedIn: false,
    locale: "",
    name: "",
    picture: "",
    zoneinfo: ""
};

const LoggedInUserContext = createContext<LoggedInUser>({
    requestInProgress: true,
    ...DEFAULT_LOGGED_OUT_USER_STATE
});

/**
 * Custom hook to return LoggedInUserContext.
 * Returns `DEFAULT_LOGGED_IN_USER_STATE` by default until the user profile is fetched via `stargateClient.me()`.
 * @returns @type {LoggedInUser}
 */
export const useLoggedInUser = (): LoggedInUser => {
    const context = useContext(LoggedInUserContext);
    if (!context) {
        throw new Error(
            "calls to useLoggedInUser must be wrapped in <LoggedInUserProvider>"
        );
    }

    return context;
};

export const LoggedInUserProvider = ({ children }: PropsWithChildren) => {
    const stargateClient = useBxpWebStargateClient();
    const [loggedInUserInstance, setLoggedInUserInstance] =
        useState<LoggedInUser>({
            requestInProgress: true,
            ...DEFAULT_LOGGED_OUT_USER_STATE
        });

    useEffect(() => {
        const getUserProfile = async () => {
            return await stargateClient.me({});
        };

        getUserProfile()
            .then((userProfile) => {
                setLoggedInUserInstance({
                    isUserLoggedIn: true,
                    ...userProfile
                });
            })
            .catch((e) => {
                console.warn("Error getting user profile: ", e);
                setLoggedInUserInstance(DEFAULT_LOGGED_OUT_USER_STATE);
            });
    }, [stargateClient]);

    return (
        <LoggedInUserContext.Provider value={loggedInUserInstance}>
            {children}
        </LoggedInUserContext.Provider>
    );
};
